import { useMemo, useState, useCallback } from 'react';

import { LoadingButton } from '@mui/lab';
import { CheckCircle, DownloadOutlined, Error as ErrorIcon } from '@mui/icons-material';
import {
  Box,
  Chip,
  Alert,
  Paper,
  Stack,
  Table,
  Button,
  Dialog,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
  TableContainer,
  TablePagination,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import CSVTemplate from 'src/assets/templates/admin_import_candidates.csv?url';
import { useCsvImportCandidatesMutation } from 'src/services/candidates/candidates.service';

import CSVReader, { CSVReaderResult } from 'src/components/csv/csv-reader';

interface CandidateCSVRow {
  first_name: string;
  last_name: string;
  position_title: string;
  email: string;
  country_code?: string;
  phone_number?: string;
  special_information?: string;
  linkedin_link?: string;
  x_link?: string;
  facebook_link?: string;
  github_link?: string;
}

interface ImportResult {
  email: string;
  status: 'success' | 'failed';
  message?: string;
}

type Props = {
  open: boolean;
  organizationId?: string | undefined;
  onClose: VoidFunction;
};

enum ImportStep {
  UPLOAD = 'upload',
  PREVIEW = 'preview',
  RESULTS = 'results',
}

export default function BulkCandidateImportModal({
  open,
  onClose,
  organizationId
}: Props) {
  const { t } = useTranslate();

  const [currentStep, setCurrentStep] = useState<ImportStep>(ImportStep.UPLOAD);
  const [parsedData, setParsedData] = useState<CandidateCSVRow[]>([]);
  const [importResults, setImportResults] = useState<ImportResult[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [previewPage, setPreviewPage] = useState(0);
  const [previewRowsPerPage, setPreviewRowsPerPage] = useState(10);

  const [resultsPage, setResultsPage] = useState(0);
  const [resultsRowsPerPage, setResultsRowsPerPage] = useState(10);

  const [bulkImportCandidates, { isLoading: isImportingCandidates }] = useCsvImportCandidatesMutation();

  // expected CSV headers
  const expectedHeaders = useMemo(() => [
    'First Name',
    'Last Name',
    'Position Title',
    'Email',
    'Country Code',
    'Phone Number',
    'Special Information',
    'LinkedIn Link',
    'X Link',
    'Facebook Link',
    'Github Link',
  ], []);

  const handleCSVRead = useCallback((results: CSVReaderResult) => {
    setError(null);

    try {
      const { data } = results;

      if (!data || data.length === 0) {
        setError(t('candidates.import_modal.errors.empty_file'));
        return;
      }

      const headers = data[0] as string[];

      const hasAllRequiredHeaders = expectedHeaders
        .slice(0, 4) // First 4 are required
        .every((header) => headers.includes(header));

      if (!hasAllRequiredHeaders) {
        setError(t('candidates.import_modal.errors.invalid_headers'));
        return;
      }

      // parse rows
      const rows: CandidateCSVRow[] = [];

      for (let i = 1; i < data.length; i++) {
        const row = data[i] as string[];

        // skip empty rows
        if (!row || row.every((cell) => !cell || cell.trim() === '')) {
          // eslint-disable-next-line no-continue
          continue;
        }

        const firstName = row[headers.indexOf('First Name')]?.trim() || '';
        const lastName = row[headers.indexOf('Last Name')]?.trim() || '';
        const positionTitle = row[headers.indexOf('Position Title')]?.trim() || '';
        const email = row[headers.indexOf('Email')]?.trim() || '';
        const countryCode = row[headers.indexOf('Country Code')]?.trim() || '';
        const phoneNumber = row[headers.indexOf('Phone Number')]?.trim() || '';

        // Validate required fields
        if (!firstName || !lastName || !positionTitle || !email) {
          // eslint-disable-next-line no-continue
          continue;
        }

        rows.push({
          first_name: firstName,
          last_name: lastName,
          position_title: positionTitle,
          email,
          country_code: countryCode || undefined,
          phone_number: phoneNumber || undefined,
          special_information: row[headers.indexOf('Special Information')]?.trim() || '',
          linkedin_link: row[headers.indexOf('LinkedIn Link')]?.trim() || '',
          x_link: row[headers.indexOf('X Link')]?.trim() || '',
          facebook_link: row[headers.indexOf('Facebook Link')]?.trim() || '',
          github_link: row[headers.indexOf('Github Link')]?.trim() || '',
        });
      }

      if (rows.length === 0) {
        setError(t('candidates.import_modal.errors.no_valid_rows'));
        return;
      }

      setParsedData(rows);
      setCurrentStep(ImportStep.PREVIEW);
    } catch (err) {
      console.error('CSV parsing error:', err);
      setError(t('candidates.import_modal.errors.parse_failed'));
    }
  }, [t, expectedHeaders]);

  const handleImport = useCallback(async () => {
    setIsImporting(true);
    setError(null);

    try {

      const resultResponse = await bulkImportCandidates({
        organization_id: organizationId,
        candidates: parsedData,
      }).unwrap();

      setImportResults(resultResponse);
      setCurrentStep(ImportStep.RESULTS);
    } catch (err) {
      console.error('Import error:', err);
      setError(t('candidates.import_modal.errors.import_failed'));
    } finally {
      setIsImporting(false);
    }
  }, [parsedData, t, bulkImportCandidates, organizationId]);

  const handleClose = useCallback(() => {
    setCurrentStep(ImportStep.UPLOAD);
    setParsedData([]);
    setImportResults([]);
    setError(null);
    setPreviewPage(0);
    setResultsPage(0);
    onClose();
  }, [onClose]);

  const handleBackToUpload = useCallback(() => {
    setCurrentStep(ImportStep.UPLOAD);
    setParsedData([]);
    setError(null);
    setPreviewPage(0);
  }, []);

  const resultStats = useMemo(() => {
    const successful = importResults.filter((r) => r.status === 'success').length;
    const failed = importResults.filter((r) => r.status === 'failed').length;
    return { successful, failed, total: importResults.length };
  }, [importResults]);

  const renderUploadStep = () => (
    <Stack spacing={3}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Alert severity="info">
        {t('candidates.import_modal.upload_instructions')}
      </Alert>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {t('candidates.import_modal.required_fields')}:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {expectedHeaders.slice(0, 4).map((header) => (
            <Chip key={header} label={header} size="small" color="primary" />
          ))}
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {t('candidates.import_modal.optional_fields')}:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {expectedHeaders.slice(4).map((header) => (
            <Chip key={header} label={header} size="small" variant="outlined" />
          ))}
        </Stack>
      </Box>

      <CSVReader onRead={handleCSVRead} />
    </Stack>
  );

  const renderPreviewStep = () => {
    const paginatedData = parsedData.slice(
      previewPage * previewRowsPerPage,
      previewPage * previewRowsPerPage + previewRowsPerPage
    );

    return (
      <Stack spacing={2}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Alert severity="success">
          {t('candidates.import_modal.preview_success', { count: parsedData.length })}
        </Alert>

        <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{t('candidates.import_modal.table.first_name')}</TableCell>
                <TableCell>{t('candidates.import_modal.table.last_name')}</TableCell>
                <TableCell>{t('candidates.import_modal.table.position')}</TableCell>
                <TableCell>{t('candidates.import_modal.table.email')}</TableCell>
                <TableCell>{t('candidates.import_modal.table.country_code')}</TableCell>
                <TableCell>{t('candidates.import_modal.table.phone_number')}</TableCell>
                <TableCell>{t('candidates.import_modal.table.special_info')}</TableCell>
                <TableCell>{t('candidates.import_modal.table.links')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => (
                <TableRow key={index} hover>
                  <TableCell>{row.first_name}</TableCell>
                  <TableCell>{row.last_name}</TableCell>
                  <TableCell>{row.position_title}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.country_code || '-'}</TableCell>
                  <TableCell>{row.phone_number || '-'}</TableCell>
                  <TableCell>
                    {row.special_information ? (
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {row.special_information}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {row.linkedin_link && <Chip label="LinkedIn" size="small" />}
                      {row.x_link && <Chip label="X" size="small" />}
                      {row.facebook_link && <Chip label="Facebook" size="small" />}
                      {row.github_link && <Chip label="Github" size="small" />}
                      {!row.linkedin_link && !row.x_link && !row.facebook_link && !row.github_link && '-'}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={parsedData.length}
          page={previewPage}
          onPageChange={(_, newPage) => setPreviewPage(newPage)}
          rowsPerPage={previewRowsPerPage}
          onRowsPerPageChange={(e) => {
            setPreviewRowsPerPage(parseInt(e.target.value, 10));
            setPreviewPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Stack>
    );
  };

  const renderResultsStep = () => {
    const paginatedResults = importResults.slice(
      resultsPage * resultsRowsPerPage,
      resultsPage * resultsRowsPerPage + resultsRowsPerPage
    );

    return (
      <Stack spacing={2}>
        <Alert severity={resultStats.failed > 0 ? 'warning' : 'success'}>
          {t('candidates.import_modal.results_summary', {
            successful: resultStats.successful,
            failed: resultStats.failed,
            total: resultStats.total,
          })}
        </Alert>

        <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width={60}>{t('candidates.import_modal.table.status')}</TableCell>
                <TableCell>{t('candidates.import_modal.table.email')}</TableCell>
                <TableCell>{t('candidates.import_modal.table.result')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedResults.map((result, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    {result.status === 'success' ? (
                      <CheckCircle color="success" fontSize="small" />
                    ) : (
                      <ErrorIcon color="error" fontSize="small" />
                    )}
                  </TableCell>
                  <TableCell>{result.email}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={result.status === 'success' ? 'success.main' : 'error.main'}
                    >
                      {result.message || '--'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={importResults.length}
          page={resultsPage}
          onPageChange={(_, newPage) => setResultsPage(newPage)}
          rowsPerPage={resultsRowsPerPage}
          onRowsPerPageChange={(e) => {
            setResultsRowsPerPage(parseInt(e.target.value, 10));
            setResultsPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {
          currentStep === ImportStep.UPLOAD && (
            <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
              <Typography>
                {t('candidates.import_modal.title')}
              </Typography>

              <Button
                startIcon={<DownloadOutlined />}
                href={CSVTemplate}
                download="Candidate Import Template.csv"
              >
                {t('candidates.invites.candidate_invite_modal.download_template')}
              </Button>
            </Stack>
          )
        }
        {currentStep === ImportStep.PREVIEW && t('candidates.import_modal.preview_title')}
        {currentStep === ImportStep.RESULTS && t('candidates.import_modal.results_title')}
      </DialogTitle>

      <DialogContent>
        {currentStep === ImportStep.UPLOAD && renderUploadStep()}
        {currentStep === ImportStep.PREVIEW && renderPreviewStep()}
        {currentStep === ImportStep.RESULTS && renderResultsStep()}
      </DialogContent>

      <DialogActions>
        {currentStep === ImportStep.UPLOAD && (
          <Button onClick={handleClose}>{t('common.cancel')}</Button>
        )}

        {currentStep === ImportStep.PREVIEW && (
          <>
            <Button onClick={handleBackToUpload}>{t('common.back')}</Button>
            <LoadingButton
              variant="contained"
              onClick={handleImport}
              loading={isImporting}
            >
              {t('candidates.import_modal.import_button')}
            </LoadingButton>
          </>
        )}

        {currentStep === ImportStep.RESULTS && (
          <Button variant="contained" onClick={handleClose}>
            {t('common.close')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}